import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getLeadAssessmentResults } from '../../services/leadAssessmentApi';
import '../../print.css';

/**
 * PrintExitReadinessReport Component
 *
 * PDF-optimized print view for the Exit Readiness Assessment report.
 * This page is rendered by Playwright for PDF generation.
 */
const PrintExitReadinessReport = () => {
  const { sessionToken } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionToken) {
      loadResults();
    }
  }, [sessionToken]);

  const loadResults = async () => {
    try {
      setLoading(true);
      const result = await getLeadAssessmentResults(sessionToken);

      if (!result.success) {
        throw new Error(result.error);
      }

      setResults(result.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading results for PDF:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && results) {
      document.body.setAttribute('data-pdf-ready', 'true');

      // Set document title
      const businessName = results.lead_info?.business_name || 'Business';
      document.title = `${businessName} - Exit Readiness Assessment Report`;
    }
  }, [loading, results]);

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading report data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error loading report: {error}</div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">No results found</div>
      </div>
    );
  }

  const { assessment, readiness_level, recommendations, lead_info } = results;
  const scores = {
    overall: assessment.overall_exit_readiness_score,
    business: assessment.business_readiness_score,
    personal: assessment.personal_readiness_score
  };

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get responses by category
  const businessResponses = assessment.responses?.filter(r => r.category === 'business') || [];
  const personalResponses = assessment.responses?.filter(r => r.category === 'personal') || [];

  // Helper function to get score color based on gap zones
  const getScoreColor = (score) => {
    if (score > 86) return '#a07d5a';      // No gaps - bronze/tan
    if (score > 72) return '#6ba86e';      // Minor gaps - green
    if (score > 57) return '#6ca5d6';      // Considerable gaps - blue
    if (score > 43) return '#d4c563';      // Critical gaps - yellow
    if (score > 28) return '#d9a066';      // Very critical gaps - orange
    return '#d97373';                       // Extremely critical - red
  };

  // Helper function to get text color for score (ensure readability)
  const getScoreTextColor = (score) => {
    return '#1f2937';  // Dark text for all scores in print for readability
  };

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="cover-header">
          <h1>Exit Readiness Assessment Results</h1>
          <div className="report-type">Business Exit Planning Overview</div>
        </div>

        <div className="cover-business-info">
          <h2>{lead_info?.business_name || 'Business Name'}</h2>
          {lead_info?.full_name && <p className="industry">{lead_info.full_name}</p>}
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Exit Readiness Assessment</p>
        </div>
      </div>

      {/* Introduction & Purpose Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Introduction & Purpose</h1>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.1in' }}>
          Why This Assessment Matters
        </h2>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.1in' }}>
          Every business owner will exit their business—the only question is whether that exit happens on your
          terms or someone else's. Whether through a strategic sale, family succession, management buyout, or an
          unexpected life event, the transition of your business represents one of the most significant financial
          and personal decisions you will ever make.
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.1in' }}>
          Yet most business owners spend more time planning a two-week vacation than preparing for the day they
          will leave their life's work.
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.15in' }}>
          This Exit Readiness Assessment Overview provides you with a clear, honest snapshot of where you stand
          today across the critical dimensions that determine whether your eventual exit will be successful,
          fulfilling, and aligned with your vision for the future.
        </p>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in' }}>What Is Exit Planning?</h2>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.1in' }}>
          Exit planning is the intentional, strategic process of preparing both you and your business for a
          future transition—regardless of when or how that transition occurs. It is not simply "selling your
          business." Rather, it is a comprehensive approach that addresses three fundamental questions:
        </p>
        <ul style={{ fontSize: '9pt', lineHeight: '1.5', marginLeft: '0.2in', marginBottom: '0.15in' }}>
          <li><strong>What do you want?</strong> — Clarifying your personal, financial, and legacy goals for life after business ownership.</li>
          <li><strong>What do you have?</strong> — Understanding the true value of your business and your personal wealth position today.</li>
          <li><strong>What's the gap?</strong> — Identifying the distance between where you are and where you need to be—and creating a plan to close it.</li>
        </ul>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.15in' }}>
          Effective exit planning recognizes that your business is likely your largest asset, yet it is also your
          least liquid one. Without proper preparation, owners often discover too late that their business cannot
          fund the life they envisioned, or that the transition leaves relationships, legacy, and purpose unfulfilled.
        </p>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in' }}>The Value Acceleration Methodology</h2>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.1in' }}>
          This assessment is built upon the Value Acceleration Methodology—a proven framework used by Certified Exit
          Planning Advisors (CEPAs) to help business owners systematically increase the value, transferability, and
          readiness of their businesses while simultaneously preparing themselves and their families for transition.
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
          The methodology focuses on three critical areas:
        </p>
        <ul style={{ fontSize: '9pt', lineHeight: '1.5', marginLeft: '0.2in', marginBottom: '0.15in' }}>
          <li>
            <p style={{ fontSize: '8pt', lineHeight: '1.2', marginBottom: '0.1in' }}>
              <strong>- The Three Gaps Framework</strong> helps you identify and address the critical distances between where your business stands today and where it needs to be to achieve your financial and personal goals. These gaps (related to business value, personal wealth, and operational profitability) are often larger than owners realize, but they are also closable with the right strategy and sufficient time. Understanding these gaps early gives you the runway to address them intentionally rather than discovering them at the worst possible moment: when you're ready to exit and it's too late to make meaningful changes.
            </p>
          </li>
          <li>
            <p style={{ fontSize: '8pt', lineHeight: '1.2', marginBottom: '0.1in' }}>
              <strong>- The Four Intangible Capitals framework</strong> examines the human expertise, documented systems, customer relationships, and market reputation that make your business valuable independent of your personal involvement. These capitals determine whether a buyer sees your company as a risky, owner-dependent operation or as a well-structured enterprise capable of thriving under new ownership. For owners pursuing any form of exit (sale, succession, or legacy transfer) strengthening these capitals is the most direct path to increasing both value and attractiveness.
            </p>
          </li>
          <li>
            <p style={{ fontSize: '8pt', lineHeight: '1.2', marginBottom: '0.1in' }}>
              <strong>- The Five Ds framework</strong> ensures you have considered and prepared for these involuntary transitions before they occur. Owners who have addressed the Five Ds protect their families from crisis decision-making, preserve the value they have built, and maintain control even when circumstances seem beyond their control. Those who have not prepared often watch decades of work unravel in a matter of months, not because the business failed, but because no plan existed for the unexpected.
            </p>
          </li>
        </ul>
      </div>

      {/* Introduction Continued - Methodology Details */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">The Value Acceleration Framework</h1>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.3in' }}>The Three Gaps</h2>
        <div style={{ marginLeft: '0.1in', marginBottom: '0.15in' }}>
          <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
            <strong>The Value Gap</strong> — The difference between what your business is worth today and what it
            needs to be worth to meet your financial goals.
          </p>
          <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
            <strong>The Wealth Gap</strong> — The difference between your current personal net worth and what you
            need to maintain your desired lifestyle after exit.
          </p>
          <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.15in' }}>
            <strong>The Profit Gap</strong> — The difference between your current profitability and what is
            achievable through operational improvements and value drivers.
          </p>
        </div>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.3in' }}>The Four Intangible Capitals</h2>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
          Business value is driven primarily by intangible assets that sophisticated buyers evaluate carefully:
        </p>
        <div style={{ marginLeft: '0.1in', marginBottom: '0.15in' }}>
          <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
            <strong>Human Capital</strong> — The strength, depth, and retention of your leadership team and workforce.
          </p>
          <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
            <strong>Structural Capital</strong> — Your documented systems, processes, and intellectual property
            that enable the business to operate without you.
          </p>
          <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
            <strong>Customer Capital</strong> — The diversity, loyalty, and contractual strength of your customer relationships.
          </p>
          <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.15in' }}>
            <strong>Social Capital</strong> — Your reputation, brand equity, and strategic relationships in the marketplace.
          </p>
        </div>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.3in' }}>Preparation for the Five Ds</h2>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
          Wise planning also addresses the involuntary exits that can occur without warning:
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', fontWeight: '600', marginBottom: '0.08in', marginLeft: '0.1in' }}>
          Death, Disability, Divorce, Distress, and Disagreement
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.15in' }}>
          These events happen to business owners every day. Those who have prepared in advance protect their families,
          their employees, and their legacy. Those who have not often see decades of work unravel in months.
        </p>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.3in' }}>A Stewardship Perspective</h2>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
          At Dominion Business Advisors, we believe that business ownership is a form of stewardship. The enterprise
          you have built represents not only financial value but also the livelihoods of employees, the trust of
          customers, and the opportunity to leave a meaningful legacy for future generations.
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5' }}>
          Preparing well for your exit is not merely a financial exercise—it is an act of wisdom, responsibility,
          and care for those who depend on what you have built. Whether your vision includes generational succession,
          Kingdom impact, or simply ensuring that your years of sacrifice result in lasting security for your family,
          intentional planning is the path forward.
        </p>
      </div>

      {/* Executive Summary Page - Now Page 3 */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Executive Summary</h1>

        <div style={{ marginBottom: '0.2in' }}>
          <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in' }}>Overall Exit Readiness Score</h2>
          <div style={{
            background: readiness_level.color,
            color: 'white',
            padding: '0.15in',
            borderRadius: '0.08in',
            marginBottom: '0.15in'
          }}>
            <div style={{ fontSize: '28pt', fontWeight: '700', textAlign: 'center' }}>
              {Math.round(scores.overall)}%
            </div>
            <div style={{ fontSize: '11pt', fontWeight: '600', textAlign: 'center', marginTop: '0.05in' }}>
              {readiness_level.label}
            </div>
          </div>
          <p style={{ fontSize: '9pt', lineHeight: '1.5', color: '#4b5563' }}>
            {readiness_level.description}
          </p>
        </div>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.2in' }}>Score Breakdown</h2>
        <table className="metrics-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Score</th>
              <th>Interpretation</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: '600' }}>Business Readiness</td>
              <td>{Math.round(scores.business)}%</td>
              <td>{scores.business >= 67 ? 'Strong' : scores.business >= 50 ? 'Moderate' : 'Needs Improvement'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: '600' }}>Personal Readiness</td>
              <td>{Math.round(scores.personal)}%</td>
              <td>{scores.personal >= 67 ? 'Strong' : scores.personal >= 50 ? 'Moderate' : 'Needs Improvement'}</td>
            </tr>
            <tr style={{ fontWeight: '700', background: '#f9fafb' }}>
              <td>Overall Exit Readiness</td>
              <td>{Math.round(scores.overall)}%</td>
              <td>{readiness_level.label}</td>
            </tr>
          </tbody>
        </table>

        <div className="disclosure-box" style={{ marginTop: '0.2in' }}>
          <h3>About This Assessment</h3>
          <p>
            This exit readiness assessment provides a high-level evaluation of your business and personal
            preparedness for a successful exit. The score reflects where you stand on key factors that impact
            exit value and transition success.
          </p>
          <p style={{ marginTop: '0.08in' }}>
            <strong>Note:</strong> A comprehensive exit planning engagement includes in-depth financial analysis,
            business valuation, tax optimization strategies, and a customized action roadmap.
          </p>
        </div>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.2in' }}>Understanding Your Score</h2>
        <h3 style={{ fontSize: '10pt', color: '#374151', marginBottom: '0.08in' }}>Assessment Score Scaling (17% - 100%)</h3>
        <p style={{ fontSize: '8pt', lineHeight: '1.5', marginBottom: '0.12in' }}>
          Assessment scores range from <strong>17%</strong> (minimum) to <strong>100%</strong> (maximum). Your score represents where your business falls within the valuation range based on readiness factors.
        </p>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0.15in' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.08in', fontWeight: '700', fontSize: '9pt' }}>17%</td>
              <td style={{ padding: '0.08in', fontWeight: '600', fontSize: '8pt' }}>Low End (0%)</td>
              <td style={{ padding: '0.08in', fontSize: '8pt', color: '#6b7280' }}>Beginning journey, significant preparation needed</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.08in', fontWeight: '700', fontSize: '9pt' }}>58%</td>
              <td style={{ padding: '0.08in', fontWeight: '600', fontSize: '8pt' }}>Average (49%)</td>
              <td style={{ padding: '0.08in', fontSize: '8pt', color: '#6b7280' }}>Midpoint of valuation range, moderate readiness</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#ecfdf5' }}>
              <td style={{ padding: '0.08in', fontWeight: '700', fontSize: '9pt', color: '#065f46' }}>67%</td>
              <td style={{ padding: '0.08in', fontWeight: '600', fontSize: '8pt', color: '#065f46' }}>Green Zone (60%)</td>
              <td style={{ padding: '0.08in', fontSize: '8pt', color: '#065f46' }}>Well positioned for exit, strong fundamentals</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#ecfdf5' }}>
              <td style={{ padding: '0.08in', fontWeight: '700', fontSize: '9pt', color: '#065f46' }}>72%</td>
              <td style={{ padding: '0.08in', fontWeight: '600', fontSize: '8pt', color: '#065f46' }}>Best-in-Class (66%)</td>
              <td style={{ padding: '0.08in', fontSize: '8pt', color: '#065f46' }}>Elite level readiness, optimal exit positioning</td>
            </tr>
            <tr>
              <td style={{ padding: '0.08in', fontWeight: '700', fontSize: '9pt' }}>100%</td>
              <td style={{ padding: '0.08in', fontWeight: '600', fontSize: '8pt' }}>High End (100%)</td>
              <td style={{ padding: '0.08in', fontSize: '8pt', color: '#6b7280' }}>Maximum valuation potential achieved</td>
            </tr>
          </tbody>
        </table>

        <p style={{ fontSize: '8pt', lineHeight: '1.5', color: '#4b5563', fontStyle: 'italic' }}>
          <strong>Linear scaling:</strong> A 58% assessment represents an average business at approximately 49% of your valuation range. Scores of 67% and above place you in the top tier of businesses ready for exit.
        </p>
      </div>

      {/* Business Questions Detail Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Business Readiness Responses</h1>

        <p style={{ marginBottom: '0.15in', fontSize: '9pt', lineHeight: '1.5' }}>
          Your responses to the business readiness assessment questions:
        </p>

        <table className="metrics-table">
          <thead>
            <tr>
              <th style={{ width: '70%' }}>Question</th>
              <th style={{ width: '30%' }}>Score</th>
            </tr>
          </thead>
          <tbody>
            {businessResponses.map((response, index) => {
              const isNA = response.answer_value === 0;
              return (
                <tr key={index} style={{ backgroundColor: isNA ? '#e5e7eb' : 'transparent' }}>
                  <td style={{ fontSize: '8pt', lineHeight: '1.4' }}>{response.question_text}</td>
                  <td style={{
                    fontWeight: '600',
                    textAlign: 'center',
                    backgroundColor: isNA ? '#9ca3af' : getScoreColor(response.score),
                    color: isNA ? '#ffffff' : getScoreTextColor(response.score),
                    padding: '0.08in'
                  }}>
                    {isNA ? 'N/A' : `${Math.round(response.score)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Personal Questions Detail Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Personal Readiness Responses</h1>

        <p style={{ marginBottom: '0.15in', fontSize: '9pt', lineHeight: '1.5' }}>
          Your responses to the personal readiness assessment questions:
        </p>

        <table className="metrics-table">
          <thead>
            <tr>
              <th style={{ width: '70%' }}>Question</th>
              <th style={{ width: '30%' }}>Score</th>
            </tr>
          </thead>
          <tbody>
            {personalResponses.map((response, index) => {
              const isNA = response.answer_value === 0;
              return (
                <tr key={index} style={{ backgroundColor: isNA ? '#e5e7eb' : 'transparent' }}>
                  <td style={{ fontSize: '8pt', lineHeight: '1.4' }}>{response.question_text}</td>
                  <td style={{
                    fontWeight: '600',
                    textAlign: 'center',
                    backgroundColor: isNA ? '#9ca3af' : getScoreColor(response.score),
                    color: isNA ? '#ffffff' : getScoreTextColor(response.score),
                    padding: '0.08in'
                  }}>
                    {isNA ? 'N/A' : `${Math.round(response.score)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Business Valuation Context Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Business Valuation Context</h1>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in' }}>Financial Overview</h2>
        <table className="info-table">
          <tbody>
            {assessment.annual_revenue && (
              <tr>
                <th>Annual Revenue</th>
                <td>${assessment.annual_revenue.toLocaleString()}</td>
              </tr>
            )}
            {assessment.ebitda_amount && (
              <tr>
                <th>EBITDA</th>
                <td>${assessment.ebitda_amount.toLocaleString()}</td>
              </tr>
            )}
            {assessment.industry && (
              <tr>
                <th>Industry</th>
                <td>{assessment.industry}</td>
              </tr>
            )}
            {assessment.valuation_multiple && (
              <tr>
                <th>EBITDA Multiple</th>
                <td>{parseFloat(assessment.valuation_multiple).toFixed(1)}x</td>
              </tr>
            )}
          </tbody>
        </table>

        {assessment.valuation_low && assessment.valuation_median && assessment.valuation_high && (
          <>
            <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.2in' }}>
              Estimated Valuation Range
            </h2>
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Range</th>
                  <th>Valuation</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight: '600' }}>Low End</td>
                  <td>${assessment.valuation_low.toLocaleString()}</td>
                  <td>Conservative estimate</td>
                </tr>
                <tr style={{ background: '#ecfdf5' }}>
                  <td style={{ fontWeight: '700', color: '#1e3a8a' }}>Median</td>
                  <td style={{ fontWeight: '700' }}>${assessment.valuation_median.toLocaleString()}</td>
                  <td style={{ fontWeight: '600' }}>Most likely valuation</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>High End</td>
                  <td>${assessment.valuation_high.toLocaleString()}</td>
                  <td>Optimistic estimate</td>
                </tr>
              </tbody>
            </table>
            <p style={{ fontSize: '8pt', color: '#6b7280', marginTop: '0.1in', fontStyle: 'italic' }}>
              {assessment.valuation_multiple
                ? `* Valuation range based on ${parseFloat(assessment.valuation_multiple).toFixed(1)}x EBITDA multiple applied to your ${assessment.ebitda_amount?.toLocaleString()} EBITDA. A comprehensive valuation requires detailed financial analysis and quality of earnings review.`
                : `* Valuation range based on typical ${assessment.industry} industry multiples applied to EBITDA. A comprehensive valuation requires detailed financial analysis and quality of earnings review.`
              }
            </p>
          </>
        )}

        {assessment.wealth_gap_amount && assessment.wealth_gap_amount > 0 && (
          <>
            <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in', marginTop: '0.2in' }}>
              Wealth Gap Analysis
            </h2>
            <table className="info-table">
              <tbody>
                <tr>
                  <th>Your Wealth Goal</th>
                  <td>${assessment.desired_exit_proceeds?.toLocaleString() || 'Not specified'}</td>
                </tr>
                <tr>
                  <th>Current Net Worth (Outside Business)</th>
                  <td>${assessment.current_net_worth?.toLocaleString() || '0'}</td>
                </tr>
                <tr style={{ background: '#fef3c7', fontWeight: '700' }}>
                  <th style={{ color: '#92400e' }}>Wealth Gap to Fill</th>
                  <td style={{ color: '#92400e' }}>${Math.abs(assessment.wealth_gap_amount).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
            <div className="disclosure-box" style={{ marginTop: '0.15in' }}>
              <h3>What This Means</h3>
              <p>
                Your business needs to generate <strong>${Math.abs(assessment.wealth_gap_amount).toLocaleString()}
                  in after-tax proceeds</strong> to bridge the gap between your current net worth and your wealth goal.
              </p>
              <p style={{ marginTop: '0.08in' }}>
                Strategies to address this gap include: increasing business value through strategic growth initiatives,
                adjusting exit timeline to allow for value growth, optimizing tax structure to preserve more proceeds,
                or exploring creative deal structures (earnouts, seller financing, equity rollover).
              </p>
            </div>
          </>
        )}
      </div>

      {/* Recommendations Pages */}
      {recommendations && recommendations.length > 0 && (
        <>
          <div className="print-page">
            <div className="pdf-header">
              <img src="/dominion-header.png" alt="Dominion Business Advisors" />
            </div>
            <h1 className="section-title">Personalized Recommendations</h1>

            <p style={{ marginBottom: '0.15in', fontSize: '9pt', lineHeight: '1.5' }}>
              Based on your assessment results, here are the priority areas to focus on to improve your exit readiness:
            </p>

            {recommendations.slice(0, 3).map((rec, index) => (
              <div key={index} style={{ marginBottom: '0.2in', pageBreakInside: 'avoid' }}>
                <h3 style={{
                  fontSize: '10pt',
                  color: '#1e3a8a',
                  marginBottom: '0.08in',
                  borderBottom: '2pt solid #1e3a8a',
                  paddingBottom: '0.05in'
                }}>
                  Priority {index + 1}: {rec.title}
                </h3>
                <p style={{ fontSize: '9pt', marginBottom: '0.08in', lineHeight: '1.5' }}>
                  {rec.message}
                </p>
                <div style={{ marginLeft: '0.15in' }}>
                  <p style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.05in' }}>Action Items:</p>
                  <ul style={{ margin: '0', paddingLeft: '0.2in', fontSize: '8pt', lineHeight: '1.6' }}>
                    {rec.action_items.map((item, idx) => (
                      <li key={idx}>- {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {recommendations.length > 3 && (
            <div className="print-page">
              <div className="pdf-header">
                <img src="/dominion-header.png" alt="Dominion Business Advisors" />
              </div>
              <h1 className="section-title">Additional Recommendations</h1>

              {recommendations.slice(3).map((rec, index) => (
                <div key={index} style={{ marginBottom: '0.2in', pageBreakInside: 'avoid' }}>
                  <h3 style={{
                    fontSize: '10pt',
                    color: '#1e3a8a',
                    marginBottom: '0.08in',
                    borderBottom: '2pt solid #1e3a8a',
                    paddingBottom: '0.05in'
                  }}>
                    Priority {index + 4}: {rec.title}
                  </h3>
                  <p style={{ fontSize: '9pt', marginBottom: '0.08in', lineHeight: '1.5' }}>
                    {rec.message}
                  </p>
                  <div style={{ marginLeft: '0.15in' }}>
                    <p style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.05in' }}>Action Items:</p>
                    <ul style={{ margin: '0', paddingLeft: '0.2in', fontSize: '8pt', lineHeight: '1.6' }}>
                      {rec.action_items.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Next Steps Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Next Steps</h1>

        <h2 style={{ fontSize: '11pt', color: '#1e3a8a', marginBottom: '0.1in' }}>
          Consider engaging with Dominion Business Advisors for a Comprehensive Exit Planning Assessment
        </h2>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.1in', fontWeight: '600' }}>
          This overview is a starting point, not a destination.
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.15in' }}>
          The patterns you discover here deserve deeper exploration. A high-level self-assessment cannot replace
          the insight that comes from working with experienced advisors who understand the complexities of exit
          planning, value acceleration, and the unique dynamics of privately held businesses.
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.1in' }}>
          Dominion Business Advisors offers a comprehensive, personalized assessment process that goes far beyond
          what any self-guided tool can provide. Through our full diagnostic suite, we help business owners:
        </p>
        <ul style={{ fontSize: '9pt', lineHeight: '1.6', marginLeft: '0.2in', marginBottom: '0.15in' }}>
          <li>- Identify the specific value drivers and risk factors affecting their business valuation</li>
          <li>- Quantify the gaps between current position and desired outcomes</li>
          <li>- Develop a prioritized, 90-day action plan tied to measurable progress</li>
          <li>- Align business strategy with personal goals, family considerations, and legacy vision</li>
          <li>- Build a team of coordinated advisors (financial, legal, tax, and operational) working toward a unified plan</li>
        </ul>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.15in' }}>
          Whether your exit is three years away or ten, the time to begin preparing is now. The owners who achieve
          the best outcomes (financially, personally, and in terms of legacy) are those who start early and work intentionally.
        </p>

        <div className="disclosure-box" style={{ background: '#ecfdf5', borderColor: '#10b981', marginBottom: '0.15in' }}>
          <h3 style={{ color: '#065f46' }}>When You Are Ready</h3>
          <p>
            When you are ready to move from overview to action, we invite you to connect with Dominion Business
            Advisors for a confidential conversation about your situation and goals.
          </p>
        </div>

        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.08in' }}>
          Your business deserves a thought process that honors what you have built. Your family deserves a plan
          that protects what matters most. And you deserve an exit that reflects your vision, your values, and
          the years of work you have invested.
        </p>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', fontWeight: '600', marginBottom: '0.2in' }}>
          Let us help you get there.
        </p>
        <div style={{ marginBottom: '0.2in' }}>
          <p style={{ fontSize: '12pt', marginBottom: '0.08in', textAlign: 'center', fontWeight: '600' }}>
            Clarity <span style={{ fontSize: '10pt' }}>-</span> Confidence <span style={{ fontSize: '10pt' }}>-</span> Legacy
          </p>
        </div>
        <div style={{ marginBottom: '0.2in' }}>
          <h3 style={{ fontSize: '16pt', marginBottom: '0.08in', textAlign: 'center', fontWeight: '600' }}>
            Dominion Business Advisors
          </h3>
        </div>

        <div style={{
          marginTop: '0.25in',
          padding: '0.15in',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          borderRadius: '0.08in',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '12pt', marginBottom: '0.08in', color: 'white' }}>
            Schedule Your Complimentary Consultation
          </h3>
          <p style={{ fontSize: '9pt', marginBottom: '0.1in' }}>
            Let's discuss your specific situation and develop a personalized exit strategy.
          </p>
          <div style={{ fontSize: '9pt', marginTop: '0.1in' }}>
            <p style={{ marginBottom: '0.05in' }}>
              <strong>Send us an email:</strong> <a href="mailto:info@dominionbusinessadvisors.com" target="_blank" rel="noopener noreferrer">info@dominionbusinessadvisors.com</a>
            </p>
            <p style={{ marginBottom: '0.05in' }}> 
              <strong>Schedule a complimentary consultation:</strong> <a href="https://calendly.com/cameronteich/strategy-and-advisory-session" target="_blank" rel="noopener noreferrer">DominionBusinessAdvisors.com/schedule-a-meeting</a>
            </p>
          </div>
        </div>

        <div style={{ marginTop: '0.3in', textAlign: 'center', fontSize: '10pt', color: '#6b7280' }}>
          <p>
            This report is confidential and prepared specifically for {lead_info?.full_name || 'the recipient'}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrintExitReadinessReport;
